import { EditIcon, LinkIcon, PlayCircleIcon } from '@tractian/icons'
import { Avatar, Tooltip, cn } from '@tractian/ui'
import type { ColDef } from 'ag-grid-community'

import type { BuildColDef } from '@/components/Shared/ExtendedAgGrid/create-column-ctx'
import { createColumnCtx } from '@/components/Shared/ExtendedAgGrid/create-column-ctx'
import type { PlantAssessment } from '@/features/assessments/api/assessment-list'
import type { User } from '@/features/assessments/api/users-list'
import { ASSESSMENT_STATUS_CONFIG } from '@/features/assessments/domain/assessment-status'
import { resolveInitialLanguage } from '@/app/i18n'
import { DateService } from '@/services/date/DateService'
import { countryLabel } from '@/features/assessments/utils/countries'
import { redirectToPlatform } from '@/features/assessments/utils/redirect-to-platform'

function FactoryIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={cn('h-4 w-4', className)}>
      <path fill="none" d="M0 0h24v24H0z" />
      <path d="M22 22H2V10l7-3v2l5-2v3h3l1-8h3l1 8v12zM12 9.95l-5 2V10l-3 1.32V20h16v-8h-8V9.95zM11 18h2v-4h-2v4zm-4 0h2v-4H7v4zm10-4h-2v4h2v-4z" />
    </svg>
  )
}

function AtexIcon({ className }: { className?: string }) {
  return <img src="/images/atex.png" alt="ATEX" className={cn('h-4 shrink-0', className)} />
}

function HubspotIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={cn('h-4 w-4', className)}>
      <path d="M18.164 7.93V5.084a2.198 2.198 0 0 0 1.267-1.978V3.06A2.198 2.198 0 0 0 17.234.862h-.047a2.198 2.198 0 0 0-2.197 2.198v.047a2.198 2.198 0 0 0 1.267 1.978V7.93a6.232 6.232 0 0 0-2.965 1.297L5.754 3.545a2.44 2.44 0 0 0 .07-.555A2.454 2.454 0 1 0 3.37 5.444a2.43 2.43 0 0 0 1.374-.426l7.582 5.563a6.248 6.248 0 0 0 .036 5.628L9.923 17.65a2.06 2.06 0 0 0-.596-.089 2.09 2.09 0 1 0 2.09 2.09 2.07 2.07 0 0 0-.256-.989l2.41-1.428a6.268 6.268 0 1 0 4.593-9.304zm-.977 9.77a3.338 3.338 0 1 1 0-6.676 3.338 3.338 0 0 1 0 6.676z" />
    </svg>
  )
}

export type AssessmentColumnSharedProps = {
  t: (key: string) => string
  usersMap: Map<string, User>
  isUsersLoading: boolean
  hiddenCols: string[]
  // With multiPhase: no Status column and no Start action, and Open Platform
  // shows for assessments with a company and a phase already sent (syncedAssessmentIds).
  multiPhase?: boolean
  syncedAssessmentIds?: ReadonlySet<string>
  onStartAssessment: (assessment: PlantAssessment) => void
  onEditAssessment: (assessment: PlantAssessment) => void
}

type Params = { data?: PlantAssessment; value?: unknown }

const COLUMN_DEFS: BuildColDef<PlantAssessment, AssessmentColumnSharedProps>[] = [
  {
    field: 'plantName',
    headerName: 'Plant',
    sortable: true,
    minWidth: 180,
    flex: 2,
    cellClass: '!p-0',
    cellRenderer: ({ data }: Params) => {
      if (!data) return null
      return (
        <div className="flex h-full items-center gap-2 px-4">
          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-blue-50">
            <FactoryIcon className="h-4 w-4 text-blue-500" />
          </div>
          <span className="truncate text-sm text-gray-800">{data.plantName}</span>
          {data.isAtex && <AtexIcon />}
        </div>
      )
    }
  },
  {
    field: 'dealOwnerUserID',
    headerName: 'Deal Owner',
    minWidth: 160,
    flex: 1,
    cellClass: '!p-0',
    cellRenderer: ({ data }: Params, { usersMap, isUsersLoading }: AssessmentColumnSharedProps) => {
      if (!data) return null
      if (isUsersLoading)
        return <div className="mx-4 h-6 w-6 animate-pulse rounded-full bg-gray-200" />
      const user = usersMap.get(data.dealOwnerUserID ?? '')
      if (!user) return <span className="px-4 text-sm text-gray-400">-</span>
      return (
        <div className="flex h-full items-center gap-2 px-4">
          <Avatar size="medium">
            <Avatar.Image src={user.imageUrl ?? undefined} />
            <Avatar.Fallback>{user.name.charAt(0).toUpperCase()}</Avatar.Fallback>
          </Avatar>
          <span className="truncate text-sm text-gray-700">{user.name}</span>
        </div>
      )
    }
  },
  {
    field: 'salesEngineerUserID',
    headerName: 'Sales Engineer',
    minWidth: 160,
    flex: 1,
    cellClass: '!p-0',
    cellRenderer: (
      { data }: Params,
      { usersMap, isUsersLoading }: AssessmentColumnSharedProps
    ) => {
      if (!data) return null
      if (isUsersLoading)
        return <div className="mx-4 h-6 w-6 animate-pulse rounded-full bg-gray-200" />
      const user = usersMap.get(data.salesEngineerUserID ?? '')
      if (!user) return <span className="px-4 text-sm text-gray-400">-</span>
      return (
        <div className="flex h-full items-center gap-2 px-4">
          <Avatar size="medium">
            <Avatar.Image src={user.imageUrl ?? undefined} />
            <Avatar.Fallback>{user.name.charAt(0).toUpperCase()}</Avatar.Fallback>
          </Avatar>
          <span className="truncate text-sm text-gray-700">{user.name}</span>
        </div>
      )
    }
  },
  {
    field: 'country',
    headerName: 'Country',
    sortable: true,
    minWidth: 120,
    flex: 1,
    cellRenderer: ({ value }: Params) => (
      <span className="text-sm text-gray-600">
        {typeof value === 'string' && value ? countryLabel(value) : '-'}
      </span>
    )
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    minWidth: 100,
    flex: 1,
    cellClass: '!p-0',
    cellRenderer: ({ value }: Params, { t }: AssessmentColumnSharedProps) => {
      if (typeof value !== 'string' || !value)
        return <span className="px-4 text-sm text-gray-400">-</span>
      const cfg = ASSESSMENT_STATUS_CONFIG[value as keyof typeof ASSESSMENT_STATUS_CONFIG]
      if (!cfg) return null
      return (
        <div className={`flex h-full items-center gap-1.5 px-4 ${cfg.colorClass}`}>
          <cfg.Icon className="h-4 w-4 shrink-0" />
          <span className="text-sm">{t(`assessmentTable.status.${value}`)}</span>
        </div>
      )
    }
  },
  {
    field: 'updatedAt',
    headerName: 'Last updated',
    sortable: true,
    minWidth: 130,
    flex: 1,
    cellRenderer: ({ value }: Params) => (
      <span className="text-sm text-gray-600">
        {typeof value === 'string' && value
          ? (DateService.formatDate(value, resolveInitialLanguage()) ?? '-')
          : '-'}
      </span>
    )
  },
  {
    headerName: '',
    sortable: false,
    pinned: 'right',
    lockPinned: true,
    suppressMovable: true,
    resizable: false,
    width: 140,
    cellClass: '!p-0',
    cellRenderer: (
      { data }: Params,
      {
        t,
        multiPhase,
        syncedAssessmentIds,
        onEditAssessment,
        onStartAssessment
      }: AssessmentColumnSharedProps
    ) => {
      if (!data) return null
      const showStart = !multiPhase && data.status === 'pending'
      const showOpenPlatform = multiPhase
        ? Boolean(data.companyID) && (syncedAssessmentIds?.has(data.id) ?? false)
        : data.status === 'completed'
      return (
        <div className="flex h-full items-center justify-end gap-1 px-2" data-actions-cell>
          {data.hubspotDealId && (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <button
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-blue-500 transition-colors hover:bg-blue-50"
                  onClick={() => {
                    window.open(
                      `https://app.hubspot.com/contacts/7933635/record/0-3/${data.hubspotDealId}/`,
                      '_blank',
                      'noreferrer'
                    )
                  }}
                >
                  <HubspotIcon />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content>{t('assessmentTable.actions.openHubspot')}</Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip>
          )}
          <Tooltip>
            <Tooltip.Trigger asChild>
              <button
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-blue-500 transition-colors hover:bg-blue-50"
                onClick={() => onEditAssessment(data)}
              >
                <EditIcon className="h-4 w-4" />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content>{t('assessmentTable.actions.edit')}</Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip>
          {showStart && (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <button
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-blue-500 transition-colors hover:bg-blue-50"
                  onClick={() => onStartAssessment(data)}
                >
                  <PlayCircleIcon className="h-4 w-4" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content>{t('assessmentTable.actions.startAssessment')}</Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip>
          )}
          {showOpenPlatform && (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <button
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-blue-500 transition-colors hover:bg-blue-50"
                  onClick={() => {
                    if (data.companyID) redirectToPlatform({ companyId: data.companyID })
                  }}
                >
                  <LinkIcon className="h-4 w-4" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content>{t('assessmentTable.actions.openPlatform')}</Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip>
          )}
        </div>
      )
    }
  }
]

export function createAssessmentColumns(
  props: AssessmentColumnSharedProps
): ColDef<PlantAssessment>[] {
  const buildColumns = createColumnCtx(props)

  const defs = props.multiPhase ? COLUMN_DEFS.filter(def => def.field !== 'status') : COLUMN_DEFS

  const defsWithVisibility = defs.map(def => {
    if (def.field && props.hiddenCols.includes(def.field as string)) {
      return { ...def, hide: true }
    }
    if (def.field) {
      return { ...def, hide: false }
    }
    return def
  })

  return buildColumns(defsWithVisibility)
}
